import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import LocationPrompt from '../components/common/LocationPrompt';
import { HiOutlineSparkles, HiOutlineDevicePhoneMobile } from 'react-icons/hi2';
import { motion } from 'framer-motion';

export default function Welcome() {
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [locationBlocked, setLocationBlocked] = useState(false);
  const rawUsername = localStorage.getItem('username') || 'User';
  const username = rawUsername.charAt(0).toUpperCase() + rawUsername.slice(1);
  const accessLevel = localStorage.getItem('level');

  const requestLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocation({ lat: latitude.toFixed(5), lon: longitude.toFixed(5) });
        setLocationDenied(false);
        setLocationBlocked(false);
        setShowPrompt(false);
      },
      () => {
        setLocation(null);
        setLocationDenied(true);
        navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
          setLocationBlocked(result.state === 'denied');
          setShowPrompt(true);
        });
      }
    );
  };

  const checkIfBlocked = () => {
    navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
      setLocationBlocked(result.state === 'denied');
      setShowPrompt(true);
    });
  };

  const checkIfUnblocked = () => {
    navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
      if (result.state !== 'denied') {
        requestLocation();
      }
    });
  };

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocation({ lat: latitude.toFixed(5), lon: longitude.toFixed(5) });
        setLocationDenied(false);
      },
      () => {
        setLocation(null);
        setLocationDenied(true);
      }
    );
  }, []);

  const handleCommissionClick = () => {
  // TODO: change the accessLevel to disable commissioning button
    if (accessLevel === 'readOnly') {
    alert('You have read-only access. Commissioning is not permitted.');
    return;
  }
    if (location?.lat && location?.lon) {
      navigate('/commission', { state: { location } });
    } else {
      checkIfBlocked();
    }
  };

  return (
    <div className="min-h-screen bg-app text-gray-800 relative">
      {showPrompt && (
        <LocationPrompt
          onRetry={requestLocation}
          onCheck={checkIfUnblocked}
          isBlocked={locationBlocked}
        />
      )}

      <Header
        location={location}
        locationDenied={locationDenied}
        onLocationClick={checkIfBlocked}
      />

      <main className="max-w-4xl mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-6"
        >
          {/* Greeting */}
          <div className="flex items-center gap-2 text-primary text-lg font-semibold">
            <HiOutlineSparkles className="w-6 h-6" />
            <span>Hello, {username}</span>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold leading-tight">
            Ready to commission your next device?
          </h1>

          {/* Description + Highlighted "Let’s begin" */}
          <p className="text-sm text-gray-600 leading-relaxed max-w-prose">
            We’ll guide you through every step: uploading the label, scanning or entering serials,
            checking signal diagnostics, and saving metadata.
          </p>
          <h1>Let’s begin!</h1>

          {/* CTA Button */}
          <button
            onClick={handleCommissionClick}
            className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition"
          >
            <HiOutlineDevicePhoneMobile className="w-5 h-5" />
            Commission a Device
          </button>
        </motion.div>
      </main>
    </div>
  );
}
